import type {
  LatLon,
  TargetState,
  WorldSnapshot,
} from "@radar-sim/schema";
import type { AreaData } from "@radar-sim/area-data";
import {
  bearingRangeToScreen,
  latLonToScreen,
  type PPIViewport,
  type ScreenXY,
} from "../coords.js";
import {
  attenuateTargetPaint,
  generateSeaClutter,
  type ClutterCell,
} from "../clutter.js";
import type { DisplayState } from "../displayState.js";
import { intensityToColor } from "../colorRamp.js";
import { rainBlockingFraction } from "../rainMask.js";
import type { MarpaTrack } from "../marpa.js";
import { morseFor } from "../morse.js";
import { bearingDeg, distanceNm } from "@radar-sim/engine";
import {
  getTidalAnomalies,
  isLineOccludedByCoast,
  lightFrame,
} from "@radar-sim/area-data";
import { buildVisibilityField, type VisibilityField } from "../visibility.js";
import {
  colourFor,
  flashRateHz,
  lightVisible,
  lightsFor,
} from "../../identification/vesselIdentity.js";

/** Snapshot of a target's past pose for echo-trail rendering. */
export interface EchoTrailSample {
  position: LatLon;
  /** Sim seconds at which the sample was taken. */
  simSeconds: number;
}

export interface DrawInputs {
  ctx: CanvasRenderingContext2D;
  width: number;
  height: number;
  snapshot: WorldSnapshot;
  display: DisplayState;
  area: AreaData | undefined;
  marpa: ReadonlyArray<MarpaTrack>;
  /** Sweep angle relative to the up-direction, in degrees. */
  sweepDeg: number;
  scenarioSeed: number;
  /**
   * Recent own-ship positions in scenario-time order (oldest first).
   * Drawn as a fading "wake" trail behind the centre — the canonical
   * visual cue that the ship is moving on a PPI where own ship is
   * always at the centre.
   */
  ownShipTrail?: ReadonlyArray<LatLon>;
  /**
   * Echo trail per vessel id. Drawn as fading colour blobs behind each
   * live target paint, mimicking the long-persistence phosphor on real
   * radar (and the explicit echo-trail feature on HD-colour units).
   */
  echoTrails?: ReadonlyMap<string, ReadonlyArray<EchoTrailSample>>;
}

const TINT = {
  bg: "#02100b",
  ringStroke: "rgba(120, 220, 160, 0.35)",
  ringText: "rgba(180, 240, 200, 0.7)",
  heading: "rgba(220, 255, 230, 0.9)",
  coastline: "rgba(160, 220, 200, 0.55)",
  mark: "rgba(255, 240, 160, 0.9)",
  raconText: "rgba(255, 240, 160, 1)",
  rain: "rgba(120, 170, 230, 0.35)",
  clutter: "rgba(120, 220, 160, 0.18)",
  target: "rgba(160, 255, 190, 1)",
  marpaVector: "rgba(255, 220, 130, 0.95)",
  marpaBuilding: "rgba(255, 220, 130, 0.5)",
  marpaLost: "rgba(255, 130, 130, 0.7)",
  ebl: "rgba(255, 130, 130, 0.85)",
  vrm: "rgba(255, 130, 130, 0.85)",
  sweep: "rgba(180, 255, 200, 0.4)",
  hud: "rgba(220, 255, 230, 0.85)",
} as const;

export function drawPPI(input: DrawInputs): void {
  const { ctx, width, height } = input;
  ctx.save();
  ctx.fillStyle = TINT.bg;
  ctx.fillRect(0, 0, width, height);

  const radiusPx = Math.min(width, height) / 2 - 24;
  const cx = width / 2;
  const cy = height / 2;
  const viewport: PPIViewport = {
    cx,
    cy,
    radiusPx,
    rangeNm: input.display.rangeNm,
    mode: input.display.mode,
    ownCog: input.snapshot.ownShip.cog,
  };

  // One per-frame visibility field — answers "is point P the foremost
  // land along its bearing from own ship?". Coastline outline, marks,
  // targets, and echo trails all consult it; everything behind a
  // closer landmass falls into shadow exactly the way a real radar's
  // beam would lose them.
  const visibility =
    input.area !== undefined
      ? buildVisibilityField(
          input.snapshot.ownShip.position,
          input.area.coastline,
          input.display.rangeNm,
        )
      : undefined;

  ctx.beginPath();
  ctx.arc(cx, cy, radiusPx, 0, Math.PI * 2);
  ctx.clip();

  drawClutter(input, viewport);
  drawRainCells(input, viewport);
  drawCoastline(input, viewport, visibility);
  drawDepthContours(input, viewport);
  drawTidalAnomalies(input, viewport);
  drawRangeRings(ctx, viewport);
  drawCompassRose(ctx, viewport);
  drawMarks(input, viewport, visibility);
  drawWake(input, viewport);
  drawHeadingLine(ctx, viewport);
  drawEchoTrails(input, viewport);
  drawTargets(input, viewport);
  if (input.display.nightMode) drawVesselLights(input, viewport);
  drawRaconTrails(input, viewport);
  drawSarts(input, viewport);
  drawMarpa(input, viewport);
  drawEBLs(ctx, input.display, viewport);
  drawVRMs(ctx, input.display, viewport);
  drawSweep(ctx, viewport, input.sweepDeg);
  drawCentreCross(ctx, cx, cy);

  ctx.restore();
  // Intentionally no in-canvas HUD: the chrome supplies range / mode /
  // gain / sea / rain / tune, and the helm panel shows course / speed
  // / sim time / position. Drawing the HUD inside the canvas
  // duplicated that info and overflowed the round border-radius mask.
}

function drawRangeRings(
  ctx: CanvasRenderingContext2D,
  v: PPIViewport,
): void {
  ctx.strokeStyle = TINT.ringStroke;
  ctx.lineWidth = 1;
  for (let r = 1; r <= 4; r++) {
    ctx.beginPath();
    ctx.arc(v.cx, v.cy, (v.radiusPx * r) / 4, 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.fillStyle = TINT.ringText;
  ctx.font = "11px ui-monospace, monospace";
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  for (let r = 1; r <= 4; r++) {
    const label = ((v.rangeNm * r) / 4).toFixed(2) + " nm";
    ctx.fillText(label, v.cx + 4, v.cy - (v.radiusPx * r) / 4);
  }
}

function drawCompassRose(
  ctx: CanvasRenderingContext2D,
  v: PPIViewport,
): void {
  ctx.strokeStyle = TINT.ringStroke;
  ctx.fillStyle = TINT.ringText;
  ctx.font = "10px ui-monospace, monospace";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  for (let b = 0; b < 360; b += 10) {
    const screen = bearingRangeToScreen(b, v.rangeNm, v);
    const tickInner = bearingRangeToScreen(b, v.rangeNm * 0.97, v);
    ctx.beginPath();
    ctx.moveTo(tickInner.x, tickInner.y);
    ctx.lineTo(screen.x, screen.y);
    ctx.stroke();
    if (b % 30 === 0) {
      const label = bearingRangeToScreen(b, v.rangeNm * 1.05, v);
      ctx.fillText(b.toString().padStart(3, "0"), label.x, label.y);
    }
  }
}

function drawHeadingLine(
  ctx: CanvasRenderingContext2D,
  v: PPIViewport,
): void {
  const tip = bearingRangeToScreen(v.ownCog, v.rangeNm, v);
  ctx.strokeStyle = TINT.heading;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(v.cx, v.cy);
  ctx.lineTo(tip.x, tip.y);
  ctx.stroke();
}

function drawCentreCross(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
): void {
  ctx.strokeStyle = TINT.heading;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(cx - 6, cy);
  ctx.lineTo(cx + 6, cy);
  ctx.moveTo(cx, cy - 6);
  ctx.lineTo(cx, cy + 6);
  ctx.stroke();
}

/**
 * Own-ship wake trail. Past positions are projected into the current
 * ship-relative frame and drawn as a fading dotted line behind the
 * centre. On a PPI own ship is always at the centre, so the wake is
 * the simplest "you are moving" cue at any time scale or speed.
 */
function drawWake(input: DrawInputs, v: PPIViewport): void {
  const trail = input.ownShipTrail;
  if (trail === undefined || trail.length < 2) return;
  const ctx = input.ctx;
  const own = input.snapshot.ownShip.position;
  const radius2 = v.radiusPx * v.radiusPx;

  ctx.lineCap = "round";
  for (let i = 1; i < trail.length; i++) {
    const a = latLonToScreen(own, trail[i - 1]!, v);
    const b = latLonToScreen(own, trail[i]!, v);
    // Quick clip — skip segments fully outside the PPI circle.
    const dxA = a.x - v.cx;
    const dyA = a.y - v.cy;
    const dxB = b.x - v.cx;
    const dyB = b.y - v.cy;
    if (dxA * dxA + dyA * dyA > radius2 && dxB * dxB + dyB * dyB > radius2) {
      continue;
    }
    // Older points fade out; newest segments are brightest. Trail head
    // (closest to centre) is the most recent past — it sits just behind
    // own ship and brightens as it ages.
    const age = i / trail.length;
    const alpha = 0.15 + 0.55 * age;
    ctx.strokeStyle = `rgba(160, 220, 235, ${alpha})`;
    ctx.lineWidth = 1.4;
    ctx.beginPath();
    ctx.moveTo(a.x, a.y);
    ctx.lineTo(b.x, b.y);
    ctx.stroke();
  }
}

function drawSweep(
  ctx: CanvasRenderingContext2D,
  v: PPIViewport,
  sweepDeg: number,
): void {
  const grad = ctx.createConicGradient(
    ((sweepDeg - 90) * Math.PI) / 180,
    v.cx,
    v.cy,
  );
  grad.addColorStop(0, "rgba(180, 255, 200, 0.45)");
  grad.addColorStop(0.04, "rgba(180, 255, 200, 0.08)");
  grad.addColorStop(0.2, "rgba(180, 255, 200, 0)");
  grad.addColorStop(1, "rgba(180, 255, 200, 0)");
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.arc(v.cx, v.cy, v.radiusPx, 0, Math.PI * 2);
  ctx.fill();
}

/**
 * Cached clutter result. Generation is deterministic in a 5-second bucket,
 * so within that bucket the rAF loop reuses the previous result rather than
 * regenerating ~400 cells per frame. Reduces clutter cost by ~60× at 60fps.
 */
const clutterCache = new WeakMap<
  CanvasRenderingContext2D,
  { key: string; cells: ClutterCell[] }
>();

function clutterKey(input: DrawInputs, v: PPIViewport): string {
  const env = input.snapshot.environment;
  const bucket = Math.floor(input.snapshot.simTimeSeconds / 5);
  return `${input.scenarioSeed}|${v.rangeNm}|${env.seaState}|${env.windFromDeg}|${input.display.sea.toFixed(2)}|${bucket}`;
}

function drawClutter(input: DrawInputs, v: PPIViewport): void {
  const key = clutterKey(input, v);
  const cached = clutterCache.get(input.ctx);
  let cells: ClutterCell[];
  if (cached !== undefined && cached.key === key) {
    cells = cached.cells;
  } else {
    cells = generateSeaClutter({
      rangeNm: v.rangeNm,
      environment: input.snapshot.environment,
      stc: input.display.sea,
      seed: input.scenarioSeed,
      simSeconds: input.snapshot.simTimeSeconds,
    });
    clutterCache.set(input.ctx, { key, cells });
  }
  const theme = input.display.theme;
  for (const cell of cells) {
    const xy = bearingRangeToScreen(cell.bearingDeg, cell.rangeNm, v);
    const r = (v.radiusPx * 0.05) / Math.max(1, v.rangeNm * 0.5);
    // Sea clutter is always a low-intensity return — push it through the
    // ramp at the cell's intensity so the colour follows the same scale
    // as everything else on the picture.
    input.ctx.fillStyle = intensityToColor(
      Math.min(0.45, cell.intensity * 0.6),
      theme,
      0.55,
    );
    input.ctx.beginPath();
    input.ctx.arc(xy.x, xy.y, r, 0, Math.PI * 2);
    input.ctx.fill();
  }
}

/**
 * Rain cells render as warm fuzzy blobs in the same colour ramp as
 * everything else on the radar. The FTC (rain) control compresses the
 * blob into a thin outline — a real-radar trick to peek through the
 * rain at vessels behind it.
 */
function drawRainCells(input: DrawInputs, v: PPIViewport): void {
  const ctx = input.ctx;
  const theme = input.display.theme;
  const ftc = input.display.rain;
  for (const cell of input.snapshot.environment.rainCells) {
    const xy = latLonToScreen(input.snapshot.ownShip.position, cell.centre, v);
    const r = (cell.radiusNm / v.rangeNm) * v.radiusPx;
    if (r < 2) continue;

    // Map cell.intensity (1..5) to ramp position. With FTC engaged the
    // peak intensity is rolled off so the cell appears as a thin shell.
    const peak = Math.min(0.85, 0.35 + cell.intensity * 0.1) * (1 - ftc * 0.6);
    const grad = ctx.createRadialGradient(xy.x, xy.y, 0, xy.x, xy.y, r);
    if (ftc < 0.2) {
      // Filled blob — solid centre, falling off to the edge.
      grad.addColorStop(0.0, intensityToColor(peak, theme, 0.85));
      grad.addColorStop(0.5, intensityToColor(peak * 0.7, theme, 0.6));
      grad.addColorStop(1.0, intensityToColor(peak * 0.4, theme, 0));
    } else {
      // FTC engaged — squeeze to an outer rim so vessels behind the
      // squall become visible.
      grad.addColorStop(0.0, "rgba(0,0,0,0)");
      grad.addColorStop(0.6, "rgba(0,0,0,0)");
      grad.addColorStop(0.85, intensityToColor(peak * 0.8, theme, 0.65));
      grad.addColorStop(1.0, intensityToColor(peak * 0.4, theme, 0));
    }
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(xy.x, xy.y, r, 0, Math.PI * 2);
    ctx.fill();
  }
}

/**
 * Coastline as proper HD radar land paint. Real radar terrain returns
 * are dense red/orange blobs with a hot saturated leading edge fading
 * inland through yellows to dim greens, plus a speckle field that
 * extends the paint a few pixels outward to simulate beam-width
 * spread. We don't have closed-polygon land masses (the coastline
 * rings are open lines), so we render the line as a multi-pass band
 * plus a speckle "fringe" — visually equivalent to the dense paint a
 * student would see on a Quantum or Fantom screen.
 */
function drawCoastline(
  input: DrawInputs,
  v: PPIViewport,
  visibility: VisibilityField | undefined,
): void {
  if (input.area === undefined) return;
  const ctx = input.ctx;
  const theme = input.display.theme;
  const ownPos = input.snapshot.ownShip.position;

  // Pre-project all rings to screen space, but split each ring at any
  // occluded vertex so a single ring (e.g. the IOW polygon) is drawn
  // only along its near-side arcs. A vertex is "visible" if it's the
  // closest land along its bearing from own ship.
  const rings: ScreenXY[][] = [];
  for (const ring of input.area.coastline) {
    if (ring.length < 2) continue;
    let current: ScreenXY[] = [];
    for (const p of ring) {
      const visible =
        visibility === undefined ? true : visibility.isVisible(p);
      if (visible) {
        current.push(latLonToScreen(ownPos, p, v));
      } else if (current.length >= 2) {
        rings.push(current);
        current = [];
      } else {
        current = [];
      }
    }
    if (current.length >= 2) rings.push(current);
  }
  if (rings.length === 0) return;

  const tracePath = (ring: ScreenXY[]): void => {
    ctx.beginPath();
    ctx.moveTo(ring[0]!.x, ring[0]!.y);
    for (let i = 1; i < ring.length; i++) {
      ctx.lineTo(ring[i]!.x, ring[i]!.y);
    }
  };

  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  // Pass 1 — wide outer wash (yellow / lime). The diffuse return that
  // bleeds into the water from beam-width spread.
  ctx.save();
  ctx.strokeStyle = intensityToColor(0.45, theme, 0.5);
  ctx.lineWidth = 9;
  for (const ring of rings) {
    tracePath(ring);
    ctx.stroke();
  }
  ctx.restore();

  // Pass 2 — orange band, the meat of the land return.
  ctx.strokeStyle = intensityToColor(0.65, theme, 0.85);
  ctx.lineWidth = 5;
  for (const ring of rings) {
    tracePath(ring);
    ctx.stroke();
  }

  // Pass 3 — red leading edge.
  ctx.strokeStyle = intensityToColor(0.85, theme, 0.95);
  ctx.lineWidth = 2.5;
  for (const ring of rings) {
    tracePath(ring);
    ctx.stroke();
  }

  // Pass 4 — saturated white core (very close-in cliffs).
  ctx.strokeStyle = intensityToColor(0.97, theme, 0.85);
  ctx.lineWidth = 1;
  for (const ring of rings) {
    tracePath(ring);
    ctx.stroke();
  }

  // Pass 5 — speckle fringe. Deterministic from sim seconds bucketed
  // at 1 Hz so it advances with the world but doesn't shimmer every
  // frame. Each speckle gets an intensity drawn from the colour ramp,
  // dropping with distance from the line for a soft fringe effect.
  const t = Math.floor(input.snapshot.simTimeSeconds);
  for (let r = 0; r < rings.length; r++) {
    const ring = rings[r]!;
    for (let i = 0; i < ring.length - 1; i++) {
      const a = ring[i]!;
      const b = ring[i + 1]!;
      const segLen = Math.hypot(b.x - a.x, b.y - a.y);
      const speckles = Math.min(14, Math.floor(segLen / 12));
      for (let s = 0; s < speckles; s++) {
        const seed =
          ((t * 7919 + r * 6421 + i * 3613 + s * 2017) >>> 0) /
          4294967296;
        const u = (s + seed) / Math.max(1, speckles);
        const px = a.x + (b.x - a.x) * u;
        const py = a.y + (b.y - a.y) * u;
        const dispRange = 6;
        const jx = ((seed * 53.91) % 1 - 0.5) * dispRange;
        const jy = (((seed * 113.7) % 1) - 0.5) * dispRange;
        const distFromLine = Math.hypot(jx, jy);
        const speckIntensity =
          0.85 - 0.55 * (distFromLine / (dispRange / 2 + 0.001));
        if (speckIntensity < 0.25) continue;
        ctx.fillStyle = intensityToColor(
          Math.max(0.25, Math.min(0.95, speckIntensity)),
          theme,
          0.9,
        );
        ctx.beginPath();
        ctx.arc(px + jx, py + jy, 0.95, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }
}

/**
 * Tidal anomaly polygons on the PPI — disturbed-water zones at
 * headlands. Painted as faint speckle + outline rather than the
 * chart's diagonal-hatch style; on a radar, broken water shows up
 * as scattered low-amplitude returns rather than a clean shape.
 * Sim-time-keyed jitter so the speckle has a faint shimmer.
 */
function drawTidalAnomalies(input: DrawInputs, v: PPIViewport): void {
  if (input.area === undefined) return;
  const anomalies = getTidalAnomalies(input.area.area);
  if (anomalies.length === 0) return;
  const ctx = input.ctx;
  for (const a of anomalies) {
    if (a.polygon.length < 3) continue;
    const pts = a.polygon.map((p) =>
      latLonToScreen(input.snapshot.ownShip.position, p, v),
    );
    // Skip if polygon is entirely outside the radar circle (cheap
    // bounding-box check).
    const allOutside = pts.every((pp) => {
      const dx = pp.x - v.cx;
      const dy = pp.y - v.cy;
      return dx * dx + dy * dy > v.radiusPx * v.radiusPx * 1.5;
    });
    if (allOutside) continue;
    ctx.save();
    // Clip to the polygon and stipple inside it.
    ctx.beginPath();
    ctx.moveTo(pts[0]!.x, pts[0]!.y);
    for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i]!.x, pts[i]!.y);
    ctx.closePath();
    ctx.clip();
    const minX = Math.min(...pts.map((p) => p.x));
    const maxX = Math.max(...pts.map((p) => p.x));
    const minY = Math.min(...pts.map((p) => p.y));
    const maxY = Math.max(...pts.map((p) => p.y));
    const dotCount = Math.max(40, Math.floor(a.severity * 200));
    const seed = fnv32(a.id) ^ Math.floor(input.snapshot.simTimeSeconds);
    let s = seed;
    const next = () => {
      // splitmix-ish — same module-private fnv32 is good enough seed.
      s = (s + 0x9e3779b9) | 0;
      let t = s;
      t = Math.imul(t ^ (t >>> 16), 0x21f0aaad);
      t = Math.imul(t ^ (t >>> 15), 0x735a2d97);
      return ((t ^ (t >>> 15)) >>> 0) / 4294967296;
    };
    ctx.fillStyle = `rgba(220, 130, 80, ${0.35 + a.severity * 0.3})`;
    for (let i = 0; i < dotCount; i++) {
      const dx = minX + next() * (maxX - minX);
      const dy = minY + next() * (maxY - minY);
      ctx.beginPath();
      ctx.arc(dx, dy, 1.2 + next() * 1.4, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
    // Faint outline so the zone reads as a defined area rather than
    // ambient noise.
    ctx.save();
    ctx.strokeStyle = `rgba(220, 130, 80, ${0.45 + a.severity * 0.3})`;
    ctx.lineWidth = 0.8;
    ctx.setLineDash([3, 2]);
    ctx.beginPath();
    ctx.moveTo(pts[0]!.x, pts[0]!.y);
    for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i]!.x, pts[i]!.y);
    ctx.closePath();
    ctx.stroke();
    ctx.restore();
  }
}

function drawDepthContours(input: DrawInputs, v: PPIViewport): void {
  if (input.area === undefined) return;
  input.ctx.strokeStyle = "rgba(120, 220, 160, 0.25)";
  input.ctx.lineWidth = 0.5;
  input.ctx.setLineDash([3, 3]);
  for (const c of input.area.depthContours) {
    if (c.line.length < 2) continue;
    input.ctx.beginPath();
    let first = true;
    for (const p of c.line) {
      const xy = latLonToScreen(input.snapshot.ownShip.position, p, v);
      if (first) {
        input.ctx.moveTo(xy.x, xy.y);
        first = false;
      } else {
        input.ctx.lineTo(xy.x, xy.y);
      }
    }
    input.ctx.stroke();
  }
  input.ctx.setLineDash([]);
}

function drawMarks(
  input: DrawInputs,
  v: PPIViewport,
  visibility: VisibilityField | undefined,
): void {
  if (input.area === undefined) return;
  if (!input.display.showMarks) return;
  const ctx = input.ctx;
  const nightMode = input.display.nightMode;
  for (const mark of input.area.marks) {
    // Marks behind closer land are in radar shadow.
    if (visibility !== undefined && !visibility.isVisible(mark.position)) {
      continue;
    }
    const xy = latLonToScreen(input.snapshot.ownShip.position, mark.position, v);
    const size = mark.type === "tower" ? 5 : 3;

    // Compute light frame up-front so we can dim the mark dot during
    // the off-phase as well as paint the on-phase halo. This makes
    // the night-mode pulse much more obvious — the mark visibly
    // strobes rather than just gaining a static halo.
    const frame =
      nightMode && mark.light !== undefined
        ? lightFrame(mark.light, input.snapshot.simTimeSeconds)
        : null;
    // Dim the mark dot when night mode is on AND the light is in
    // its off-phase. Otherwise paint at full intensity.
    const dimAlpha = frame !== null && !frame.on ? 0.35 : 1;

    // Bloom — towers/lights paint a brighter halo than buoys.
    ctx.save();
    ctx.globalAlpha = dimAlpha;
    ctx.shadowColor =
      mark.type === "tower" || mark.type === "light"
        ? "rgba(255, 240, 160, 0.95)"
        : "rgba(255, 240, 160, 0.55)";
    ctx.shadowBlur = mark.type === "tower" ? 10 : 5;
    ctx.fillStyle = TINT.mark;
    ctx.beginPath();
    ctx.arc(xy.x, xy.y, size, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // Night-mode pulse: coloured halo painted on top during the on-
    // phase. Paired with the dim above, this gives a clean "off →
    // bright pulse → off" cycle that reads like a real light.
    if (frame !== null && frame.on) {
      ctx.save();
      ctx.shadowColor = frame.color;
      ctx.shadowBlur = 16;
      ctx.fillStyle = frame.color;
      ctx.globalAlpha = frame.intensity;
      ctx.beginPath();
      ctx.arc(xy.x, xy.y, size + 2.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    // Tiny letter label sits next to the mark — the radial Morse trail
    // is what does the actual identification work and is painted later
    // in `drawRaconTrails` so it doesn't get overwritten by the target
    // and target-trail layers.
    if (mark.racon !== undefined) {
      ctx.fillStyle = TINT.raconText;
      ctx.font = "10px ui-monospace, monospace";
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";
      ctx.fillText(`${mark.racon.code}`, xy.x + size + 4, xy.y);
    }
  }
}

/**
 * Per-element radial extents for Morse painting. Distances are in
 * nautical miles of "downrange" trail extending from the RACON
 * position. Matches the visual scale on a real RACON: a dash takes
 * three times the radial space of a dot. */
const RACON_DOT_NM = 0.06;
const RACON_DASH_NM = 0.18;
const RACON_GAP_NM = 0.06;
/** Maximum range at which a RACON paints. Real units pulse at ~10–15
 *  nm; below that they overwhelm the screen. We pick the upper end
 *  because the simulator's range scales go to 72 nm. */
const RACON_MAX_RANGE_NM = 12;

function drawRaconTrails(input: DrawInputs, v: PPIViewport): void {
  if (input.area === undefined) return;
  if (!input.display.showMarks) return;
  const ctx = input.ctx;
  const own = input.snapshot.ownShip.position;
  for (const mark of input.area.marks) {
    if (mark.racon === undefined) continue;
    const range = distanceNm(own, mark.position);
    if (range > RACON_MAX_RANGE_NM) continue;
    const morse = morseFor(mark.racon.code);
    if (morse === undefined) continue;
    const bearing = bearingDeg(own, mark.position);
    paintMorseRadial(ctx, v, own, range, bearing, morse);
  }
}

/**
 * Paint a single Morse string as a sequence of dots/dashes radiating
 * outward from the start position along the line of sight from own
 * ship. Each segment is a glowing arc-shaped bar so the trail looks
 * like a real radar paint, not a CSS dotted line.
 */
function paintMorseRadial(
  ctx: CanvasRenderingContext2D,
  v: PPIViewport,
  own: LatLon,
  startRangeNm: number,
  bearingDegTrue: number,
  morse: string,
): void {
  let r = startRangeNm + RACON_GAP_NM * 2;
  // Width tapers very slightly with range so the trail reads as one
  // contiguous chain rather than disconnected pulses.
  ctx.save();
  ctx.lineCap = "round";
  ctx.shadowColor = "rgba(255, 230, 140, 0.9)";
  ctx.shadowBlur = 6;
  ctx.strokeStyle = "rgba(255, 230, 140, 0.9)";
  ctx.lineWidth = 3;
  for (const sym of morse) {
    const length = sym === "." ? RACON_DOT_NM : RACON_DASH_NM;
    const inner = bearingRangeToScreen(bearingDegTrue, r, v);
    const outer = bearingRangeToScreen(bearingDegTrue, r + length, v);
    // Skip if both ends fall outside the PPI clip.
    const insideRangeNm = v.rangeNm + 1;
    if (r > insideRangeNm) break;
    ctx.beginPath();
    ctx.moveTo(inner.x, inner.y);
    ctx.lineTo(outer.x, outer.y);
    ctx.stroke();
    r += length + RACON_GAP_NM;
  }
  ctx.restore();
}

/**
 * Vessel navigation lights painted at night. For each visible target
 * we figure out the relative bearing of own-ship from the target's
 * bow and paint each light that's visible in its arc as a small
 * coloured glow positioned around the vessel symbol.
 *
 * Sidelights (red/green) sit on the relevant beam. Sternlight goes
 * astern. Masthead sits forward. Fishing trawler "green over white"
 * stacks vertically above the symbol regardless of bearing (they're
 * all-round lights).
 */
function drawVesselLights(input: DrawInputs, v: PPIViewport): void {
  const ctx = input.ctx;
  const simSeconds = input.snapshot.simTimeSeconds;
  const coastline = input.area?.coastline;
  const ownPos = input.snapshot.ownShip.position;
  for (const t of input.snapshot.targets) {
    if (!t.visible) continue;
    const arr = lightsFor(t.vesselType, t.state);
    if (arr === undefined) continue;
    const xy = latLonToScreen(ownPos, t.position, v);
    const dx = xy.x - v.cx;
    const dy = xy.y - v.cy;
    if (dx * dx + dy * dy > v.radiusPx * v.radiusPx) continue;
    if (
      coastline !== undefined &&
      isLineOccludedByCoast(ownPos, t.position, coastline)
    ) {
      continue;
    }

    // Relative bearing from the target's bow to own ship.
    const ownBrgFromTarget = bearingDeg(t.position, input.snapshot.ownShip.position);
    const relBrg = ((ownBrgFromTarget - t.cog) % 360 + 360) % 360;

    // Cardinal directions around the symbol in screen-space depend on
    // the PPI mode. We need the screen-space bearing of the vessel's
    // bow (true cog mapped through the up-direction).
    const upDeg = upBearing(v);
    const cogScreenDeg = ((t.cog - upDeg) % 360 + 360) % 360;
    // Convert that to radians where 0 = up on canvas, sweep clockwise.
    const bowAngle = ((cogScreenDeg - 90) * Math.PI) / 180;
    const cosB = Math.cos(bowAngle);
    const sinB = Math.sin(bowAngle);
    // Forward (+bow), astern (−bow), starboard beam (+90° clockwise).
    const offset = (radial: number, lateral: number) => ({
      x: xy.x + cosB * radial - sinB * lateral,
      y: xy.y + sinB * radial + cosB * lateral,
    });

    ctx.save();
    for (const light of arr.lights) {
      if (!lightVisible(light, relBrg)) continue;
      // Flashing lights pulse at their declared rate. Steady lights
      // burn continuously. Pulse is sim-time keyed so replays match.
      const flashHz = flashRateHz(light);
      if (flashHz !== undefined) {
        const phase = (simSeconds * flashHz) % 1;
        if (phase > 0.4) continue; // ~40% duty
      }
      let pos: { x: number; y: number };
      switch (light) {
        case "starboard":
          pos = offset(2, 6);
          break;
        case "port":
          pos = offset(2, -6);
          break;
        case "stern":
          pos = offset(-7, 0);
          break;
        case "masthead":
          pos = offset(8, 0);
          break;
        case "fishing-green":
        case "all-round-red-top":
        case "pilot-white-top":
          pos = offset(0, -16);
          break;
        case "fishing-white":
        case "all-round-red-mid":
        case "pilot-red-bottom":
          pos = offset(0, -10);
          break;
        case "all-round-red-bottom":
          // Bottom of the CBD three-red column.
          pos = offset(0, -7);
          break;
        case "all-round-white-mid":
          pos = offset(0, -13);
          break;
        case "all-round-white":
          pos = offset(0, -10);
          break;
        case "yellow-flash":
          pos = offset(0, -22);
          break;
        case "tow-yellow":
          pos = offset(-7, -8);
          break;
        case "minesweep-mast":
          pos = offset(0, -19);
          break;
        case "minesweep-port-yard":
          pos = offset(2, -5);
          break;
        case "minesweep-stbd-yard":
          pos = offset(2, 5);
          break;
        case "dredger-obstructed-top":
          pos = offset(-2, -6);
          break;
        case "dredger-obstructed-bottom":
          pos = offset(-4, -6);
          break;
        case "dredger-clear-top":
          pos = offset(-2, 6);
          break;
        case "dredger-clear-bottom":
          pos = offset(-4, 6);
          break;
      }
      const colour = colourFor(light);
      ctx.shadowColor = colour;
      ctx.shadowBlur = 8;
      ctx.fillStyle = colour;
      ctx.beginPath();
      ctx.arc(pos.x, pos.y, 2, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }
}

function upBearing(v: PPIViewport): number {
  switch (v.mode) {
    case "north-up":
      return 0;
    case "head-up":
      return v.ownCog;
    case "course-up":
      return v.courseUpReferenceDeg ?? v.ownCog;
  }
}

/**
 * SART (Search and Rescue Transponder) signature changes continuously
 * with range. Real units generate a 12-pulse swept-FM response;
 * what the radar paints depends on how the SART's pulse train
 * compares to the radar's own beamwidth and timing:
 *
 *   - Long range (>~3 nm): 12 thin tick-marks radiating outward
 *     along the bearing line. The classic SART signature.
 *   - Closing (~1–3 nm): each tick widens into an arc segment as
 *     the SART's response spans more of the radar's sweep — looks
 *     like a row of "fans".
 *   - Close (<~1 nm): fans broaden until they meet, painting as a
 *     ring of arcs that gradually become full concentric circles.
 *   - Very close (<~0.3 nm): full concentric rings centred on the
 *     casualty, with the chain compressed to a small zone.
 *
 * Implementation: each of the 12 pulses paints as an arc segment
 * whose half-width in degrees grows smoothly as range decreases.
 * No hard mode switch — the visual evolves continuously, which is
 * what real radars show.
 */
const SART_DOTS = 12;
const SART_DOT_SPACING_NM = 0.6;
/** When the SART is very close, the dot ring starts to compress so
 *  all 12 pulses fit on-screen. Switch to a tighter spacing (in arc
 *  half-width terms it makes no visual difference, but it stops the
 *  outer ring overshooting the radar circle).  */
const SART_CLOSE_SPACING_NM = 0.08;

/**
 * Smoothly map SART range (nm) → arc half-width (degrees). Picked to
 * match real-world SART photography:
 *   range  10 →   ~1° (essentially a tick)
 *   range   5 →   ~3°
 *   range   3 →   ~8°
 *   range   2 →  ~18°
 *   range   1 →  ~45°
 *   range 0.5 →  ~90° (clear arc)
 *   range 0.3 → ~140°
 *   range 0.2 → 180° (full ring; arcs wrap around)
 */
function sartArcHalfWidthDeg(rangeNm: number): number {
  if (rangeNm <= 0) return 180;
  // Empirical curve: half-width ≈ 12 / range (clamped). Smooth and
  // monotonic, gives a recognisable progression from tick to ring.
  const raw = 9 / Math.max(0.1, rangeNm);
  return Math.min(180, Math.max(0.5, raw));
}

function drawSarts(input: DrawInputs, v: PPIViewport): void {
  if (input.snapshot.sarts.length === 0) return;
  const ctx = input.ctx;
  const own = input.snapshot.ownShip.position;
  // Pulse the casualty halo at ~1.5 Hz so the eye picks it out
  // immediately. Sim-time keyed for replay determinism.
  const pulse = 0.5 + 0.5 * Math.sin(input.snapshot.simTimeSeconds * 9);
  ctx.save();
  for (const sart of input.snapshot.sarts) {
    const range = distanceNm(own, sart.position);
    const bearing = bearingDeg(own, sart.position);
    const head = bearingRangeToScreen(bearing, range, v);
    // Bearing in canvas-radian space (clockwise from up). Same maths
    // as bearingRangeToScreen so the arcs sit on the right side.
    const upDeg = upBearing(v);
    const screenBearingDeg = ((bearing - upDeg) % 360 + 360) % 360;
    const bearingRad = ((screenBearingDeg - 90) * Math.PI) / 180;

    // Bright pulsing casualty marker first — must read as "go HERE".
    ctx.shadowColor = "rgba(255, 80, 80, 1)";
    ctx.shadowBlur = 18;
    ctx.fillStyle = `rgba(255, 60, 60, ${0.85 + pulse * 0.15})`;
    ctx.beginPath();
    ctx.arc(head.x, head.y, 6 + pulse * 3, 0, Math.PI * 2);
    ctx.fill();
    // Inner core for crisp positional readability.
    ctx.shadowBlur = 0;
    ctx.fillStyle = "rgba(255, 220, 220, 1)";
    ctx.beginPath();
    ctx.arc(head.x, head.y, 2.4, 0, Math.PI * 2);
    ctx.fill();

    // Arc half-width grows as range decreases. At long range it's
    // tiny → reads as a dot; close in it's wide → reads as an arc.
    const halfWidthDeg = sartArcHalfWidthDeg(range);
    const halfWidthRad = (halfWidthDeg * Math.PI) / 180;
    // Use tighter pulse spacing once we're close enough that the
    // standard 0.6 nm spacing would push the outer pulse off-screen.
    const spacing = range < 1
      ? SART_CLOSE_SPACING_NM
      : SART_DOT_SPACING_NM;
    // Pulses paint at increasing screen radius from own ship along
    // the bearing line. We stroke an arc centred on own ship, but
    // the visual reads as an arc centred on the SART because the
    // chord is short relative to the radius at typical ranges.
    ctx.strokeStyle = "rgba(255, 90, 90, 0.95)";
    ctx.lineWidth = 2.6;
    ctx.shadowColor = "rgba(255, 80, 80, 0.85)";
    ctx.shadowBlur = 8;
    ctx.lineCap = "round";
    for (let i = 1; i <= SART_DOTS; i++) {
      const r = range + i * spacing;
      if (r > v.rangeNm + 1) break;
      const radPx = (r / v.rangeNm) * v.radiusPx;
      // canvas arc() takes start/end angles in radians, where 0 = +x
      // axis (3 o'clock). Our screen-bearing 0° points up, which
      // translates to canvas-radian -π/2. We've stored that
      // adjustment in `bearingRad` already.
      ctx.beginPath();
      // If half-width has reached 180°, just stroke the full circle —
      // saves a wrap-around case in the angular arithmetic.
      if (halfWidthDeg >= 179) {
        ctx.arc(v.cx, v.cy, radPx, 0, Math.PI * 2);
      } else {
        ctx.arc(
          v.cx,
          v.cy,
          radPx,
          bearingRad - halfWidthRad,
          bearingRad + halfWidthRad,
        );
      }
      ctx.stroke();
    }
  }
  ctx.restore();
}

/**
 * Paint a single target as a colour-graded blob. Big-RCS commercial
 * vessels show as wide red/orange blobs; small yachts as tighter
 * yellow dots. Hot core fading to a cool edge via radial gradient.
 *
 * `intensityScale` scales the peak colour ramp position — used by
 * echo-trail rendering to draw faded copies behind the live target.
 */
/**
 * Per-vessel-type length-to-beam aspect ratio for the radar return.
 * Large commercial vessels paint as elongated blobs along their
 * bow-stern axis; small craft as roughly circular blobs. Helps a
 * student tell a 200m container ship from a 40-foot yacht just
 * from the paint shape, before they look at the AIS.
 */
function aspectFor(vesselType: import("@radar-sim/schema").VesselType): number {
  switch (vesselType) {
    case "container":
      return 4;
    case "ferry":
      return 3;
    case "fastcat":
      return 5;
    case "naval":
      return 4;
    case "minesweeper":
      return 3;
    case "tug":
    case "fishing":
    case "dredger":
      return 2;
    case "rib":
    case "pilot":
      return 1.5;
    case "hovercraft":
      return 1.2;
    case "yacht-power":
      return 1.3;
    case "yacht-sail":
    case "buoy":
      return 1;
  }
}

function paintTargetBlob(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  rcs: number,
  intensity: number,
  theme: import("../colorRamp.js").RadarTheme,
  intensityScale = 1,
  alphaScale = 1,
  // Optional shape hints — when supplied, paint an oriented elongated
  // blob along the vessel's COG. Used by drawTargets for live
  // targets; echo trails and other backwards-looking callers can
  // omit them and get the legacy circular blob.
  vesselType?: import("@radar-sim/schema").VesselType,
  cogScreenDeg?: number,
): void {
  // Size by RCS: a buoy at 0.05 → ~2.5 px, container at 1.0 → ~9 px.
  // Add a small intensity-driven swell so a strong return looks bigger.
  const baseSize = 2 + 7 * Math.sqrt(rcs);
  const size = baseSize + 2 * intensity;

  const peak = Math.max(0.25, Math.min(0.98, 0.45 + intensity * 0.55)) *
    intensityScale;
  const aspect = vesselType !== undefined ? aspectFor(vesselType) : 1;

  if (aspect <= 1.05 || cogScreenDeg === undefined) {
    // Circular blob — original behaviour for small craft, buoys, and
    // shape-less callers (echo trails).
    const grad = ctx.createRadialGradient(x, y, 0, x, y, size);
    grad.addColorStop(0.0, intensityToColor(peak, theme, 1 * alphaScale));
    grad.addColorStop(0.5, intensityToColor(peak * 0.75, theme, 0.85 * alphaScale));
    grad.addColorStop(1.0, intensityToColor(peak * 0.4, theme, 0));
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(x, y, size, 0, Math.PI * 2);
    ctx.fill();
    return;
  }
  // Elongated blob — translate to target, rotate to align with COG,
  // anisotropic scale to stretch along the bow-stern axis. The
  // gradient still draws from the centre so the hot core sits over
  // the geographic position.
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(((cogScreenDeg - 90) * Math.PI) / 180);
  ctx.scale(aspect, 1);
  const grad = ctx.createRadialGradient(0, 0, 0, 0, 0, size);
  grad.addColorStop(0.0, intensityToColor(peak, theme, 1 * alphaScale));
  grad.addColorStop(0.5, intensityToColor(peak * 0.75, theme, 0.85 * alphaScale));
  grad.addColorStop(1.0, intensityToColor(peak * 0.4, theme, 0));
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.arc(0, 0, size, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

function drawTargets(input: DrawInputs, v: PPIViewport): void {
  const ctx = input.ctx;
  const theme = input.display.theme;
  const coastline = input.area?.coastline;
  const ownPos = input.snapshot.ownShip.position;
  for (const t of input.snapshot.targets) {
    if (!t.visible) continue;
    const xy = latLonToScreen(ownPos, t.position, v);
    const dx = xy.x - v.cx;
    const dy = xy.y - v.cy;
    if (dx * dx + dy * dy > v.radiusPx * v.radiusPx) continue;
    // Terrain shadow: a target sitting on the far side of an island
    // can't be painted by a real radar — the landmass blocks the
    // line of sight. The simplified coastline polylines we ship are
    // good enough for this check at PPI scale.
    if (
      coastline !== undefined &&
      isLineOccludedByCoast(ownPos, t.position, coastline)
    ) {
      continue;
    }
    const blocking = rainBlockingFraction(
      input.snapshot.ownShip.position,
      t.position,
      input.snapshot.environment.rainCells,
    );
    const range = Math.sqrt(dx * dx + dy * dy) * (v.rangeNm / v.radiusPx);
    const paint = attenuateTargetPaint({
      gain: input.display.gain,
      stc: input.display.sea,
      ftc: input.display.rain,
      tune: input.display.tune,
      rangeNm: range,
      rainBlockingFraction: blocking,
    });
    const intensity = paint * t.rcs;
    if (intensity < 0.05) continue;
    // Convert true COG to screen-space so the elongated blob aligns
    // with the vessel's bow-stern axis even in head-up / course-up.
    const upDeg = upBearing(v);
    const cogScreenDeg = ((t.cog - upDeg) % 360 + 360) % 360;
    paintTargetBlob(
      ctx,
      xy.x,
      xy.y,
      t.rcs,
      intensity,
      theme,
      1,
      1,
      t.vesselType,
      cogScreenDeg,
    );
  }
}

/**
 * Echo trails — fading copies of each target's recent positions. A
 * canonical feature of HD-colour radar; on real units it's the
 * easiest way to perceive vessel motion before MARPA produces a
 * vector. Newest sample fades into the live paint; oldest sample is
 * almost invisible.
 */
function drawEchoTrails(input: DrawInputs, v: PPIViewport): void {
  const trails = input.echoTrails;
  if (trails === undefined || input.display.echoTrailSeconds <= 0) return;
  const trailLength = input.display.echoTrailSeconds;
  const now = input.snapshot.simTimeSeconds;
  const ctx = input.ctx;
  const theme = input.display.theme;
  const radius2 = v.radiusPx * v.radiusPx;

  const coastline = input.area?.coastline;
  const ownPos = input.snapshot.ownShip.position;
  for (const target of input.snapshot.targets) {
    const samples = trails.get(target.id);
    if (samples === undefined || samples.length === 0) continue;
    for (const s of samples) {
      const age = now - s.simSeconds;
      if (age <= 0 || age > trailLength) continue;
      const xy = latLonToScreen(ownPos, s.position, v);
      const dx = xy.x - v.cx;
      const dy = xy.y - v.cy;
      if (dx * dx + dy * dy > radius2) continue;
      if (
        coastline !== undefined &&
        isLineOccludedByCoast(ownPos, s.position, coastline)
      ) {
        continue;
      }
      // Older = dimmer, smaller. Linear decay from the most recent
      // sample (alphaScale=0.7) to the oldest (alphaScale=0.05).
      const t = 1 - age / trailLength;
      const alphaScale = 0.05 + 0.6 * t;
      const intensityScale = 0.55 + 0.4 * t;
      paintTargetBlob(
        ctx,
        xy.x,
        xy.y,
        target.rcs * 0.9,
        0.4,
        theme,
        intensityScale,
        alphaScale,
      );
    }
  }
}

function drawMarpa(input: DrawInputs, v: PPIViewport): void {
  for (const tr of input.marpa) {
    const target = input.snapshot.targets.find((t) => t.id === tr.vesselId);
    if (target === undefined) continue;
    const xy = latLonToScreen(input.snapshot.ownShip.position, target.position, v);
    // Acquisition ring.
    input.ctx.strokeStyle =
      tr.phase === "lost"
        ? TINT.marpaLost
        : tr.phase === "tracked"
          ? TINT.marpaVector
          : TINT.marpaBuilding;
    input.ctx.lineWidth = 1.5;
    input.ctx.beginPath();
    input.ctx.arc(xy.x, xy.y, 9, 0, Math.PI * 2);
    input.ctx.stroke();

    // Inline BRG / RNG label next to the acquisition ring. Real MFDs
    // paint these here so the navigator can read off the bearing and
    // range without looking away from the target. Style is the same
    // amber as the MARPA vector so it reads as part of the same
    // visual element.
    paintTargetBrgRng(input.ctx, xy.x, xy.y, tr, target.ais?.name);

    if (tr.phase === "acquiring") continue;
    // Draw 6-minute predicted vector.
    const sixMinNm = (target.sog * 6) / 60;
    const tip = latLonToScreenFromVector(
      target,
      sixMinNm,
      input.snapshot.ownShip.position,
      v,
    );
    input.ctx.lineWidth = tr.phase === "tracked" ? 2 : 1;
    if (tr.phase === "building") input.ctx.setLineDash([4, 3]);
    input.ctx.beginPath();
    input.ctx.moveTo(xy.x, xy.y);
    input.ctx.lineTo(tip.x, tip.y);
    input.ctx.stroke();
    input.ctx.setLineDash([]);
  }
}

function paintTargetBrgRng(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  tr: MarpaTrack,
  name: string | undefined,
): void {
  const brg = tr.bearingDeg.toFixed(0).padStart(3, "0");
  const rng = tr.rangeNm.toFixed(2);
  const cpa = tr.cpaNm.toFixed(2);
  const lines = [
    name !== undefined ? name.toUpperCase() : `T-${tr.vesselId.slice(-4).toUpperCase()}`,
    `${brg}° / ${rng}nm`,
    `CPA ${cpa}`,
  ];
  ctx.save();
  ctx.font = "11px ui-monospace, monospace";
  ctx.textBaseline = "top";
  ctx.textAlign = "left";
  // Sit the label off the upper-right of the acquisition ring; if that
  // would push it off the visible area we'll let the canvas clip it
  // (rare in practice — targets never sit at the extreme corner).
  const tx = x + 14;
  const ty = y - 22;
  // Translucent backdrop so the label reads cleanly against busy
  // clutter / land paint.
  const widths = lines.map((l) => ctx.measureText(l).width);
  const maxW = Math.max(...widths);
  ctx.fillStyle = "rgba(0, 0, 0, 0.45)";
  ctx.fillRect(tx - 2, ty - 2, maxW + 6, lines.length * 13 + 2);
  ctx.fillStyle =
    tr.phase === "lost"
      ? TINT.marpaLost
      : tr.cpaNm < 0.3
        ? "rgba(255, 130, 130, 0.95)"
        : TINT.marpaVector;
  for (let i = 0; i < lines.length; i++) {
    ctx.fillText(lines[i]!, tx, ty + i * 13);
  }
  ctx.restore();
}

function latLonToScreenFromVector(
  target: TargetState,
  distNm: number,
  ownPos: LatLon,
  v: PPIViewport,
) {
  // Project target along its cog by distNm using the schema/engine math.
  // We import lazily via a small flat-earth approximation here to keep
  // the draw module standalone.
  const rad = (target.cog * Math.PI) / 180;
  const dLatNm = distNm * Math.cos(rad);
  const dLonNm = distNm * Math.sin(rad);
  const dLat = dLatNm / 60;
  const dLon = dLonNm / (60 * Math.cos((target.position.lat * Math.PI) / 180));
  const tip = {
    lat: target.position.lat + dLat,
    lon: target.position.lon + dLon,
  };
  return latLonToScreen(ownPos, tip, v);
}

function drawEBLs(
  ctx: CanvasRenderingContext2D,
  display: DisplayState,
  v: PPIViewport,
): void {
  ctx.strokeStyle = TINT.ebl;
  ctx.lineWidth = 1;
  ctx.setLineDash([2, 4]);
  for (const ebl of display.ebl) {
    if (!ebl.visible) continue;
    const tip = bearingRangeToScreen(ebl.bearing, v.rangeNm, v);
    ctx.beginPath();
    ctx.moveTo(v.cx, v.cy);
    ctx.lineTo(tip.x, tip.y);
    ctx.stroke();
  }
  ctx.setLineDash([]);
}

function drawVRMs(
  ctx: CanvasRenderingContext2D,
  display: DisplayState,
  v: PPIViewport,
): void {
  ctx.strokeStyle = TINT.vrm;
  ctx.lineWidth = 1;
  ctx.setLineDash([5, 4]);
  for (const vrm of display.vrm) {
    if (!vrm.visible) continue;
    const r = (vrm.rangeNm / v.rangeNm) * v.radiusPx;
    if (r > v.radiusPx) continue;
    ctx.beginPath();
    ctx.arc(v.cx, v.cy, r, 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.setLineDash([]);
}

export function fnv32(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}
